import CalendarIcon from '../../assets/images/calendar.svg'
import flight from '../../assets/images/flight.svg'
import style from './BookingCard.module.scss'
import { Bookings, VehicleForBookingResponse } from '../../types/api.types'
import { ImageWithFallback } from '../../Components/Image/ImageWithFallback'
import car from '../../assets/images/card-card.webp'
import { capitalizeFirstLetter } from '../../utils/strings.util'

interface BookingCardProps {
  details: Bookings
  getVehicleData: (vehicleId: string) => VehicleForBookingResponse | null
  loadingVehicles: boolean
}

const BookingCard = ({ details, getVehicleData, loadingVehicles }: BookingCardProps) => {
  // Use the first trip segment for display

  const vehicleData = getVehicleData(details.vehicleId)

  const firstSegment = details.tripSegments?.[0]
  const scheduledDate = firstSegment ? new Date(firstSegment.scheduledAt) : new Date()
  const totalCost = { amount: details.fair, currencyCode: 'EUR' }
  const paymentStatus = details.paymentStatus
  const isRoundTrip = details.tripSegments && details.tripSegments.length > 1

  // Format date and time nicely
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Get status class for styling
  const getStatusClass = (status: string) => {
    const normalizedStatus = status.toLowerCase()
    if (normalizedStatus.includes('succeeded') || normalizedStatus.includes('completed')) {
      return 'succeeded'
    }
    if (normalizedStatus.includes('pending') || normalizedStatus.includes('processing')) {
      return 'pending'
    }
    if (normalizedStatus.includes('failed') || normalizedStatus.includes('cancelled')) {
      return 'failed'
    }
    return ''
  }

  return (
    <div className={style.rentalLocation}>
      <div className={style.header}>
        <div>
          <h4>Booking ID: {details.displayId}</h4>
          <div className={style.rentalLocation__item}>
            {/* <img src={CalendarIcon} alt="Date and time" /> */}
            <p className={style['date-time']} style={{ opacity: 0.6 }}>
              Booked on {formatDate(new Date(details.createdAt))} at{' '}
              {formatTime(new Date(details.createdAt))}
            </p>
          </div>
        </div>
        <span className={`${style.rentalLocation__status} ${getStatusClass(paymentStatus)}`}>
          {paymentStatus}
        </span>
      </div>

      <div className={style.vehicleInfoAndImageContainer}>
        <picture>
          <ImageWithFallback
            src={vehicleData?.imageUrl || car}
            alt="Booking vehicle"
            fallbackUrl={car}
          />
        </picture>
        <div className={style.vehicleInfoContainer}>
          <h3 className={style.vehicleType}>
            {capitalizeFirstLetter(vehicleData?.segment?.name || 'Sedan')}
          </h3>
          <div className={style.vehicleName}>
            {capitalizeFirstLetter(vehicleData?.name || 'Toyota Prius')}
          </div>
        </div>
      </div>

      <div className={style.rentalLocation__item}>
        <img src={CalendarIcon} alt="Date and time" />
        <p className={style['date-time']}>
          {formatDate(scheduledDate)} at {formatTime(scheduledDate)}
        </p>
      </div>

      <div className={style.rentalLocation__item}>
        <img src={flight} alt="Route" />
        <div>
          <div className={style.locations}>
            {firstSegment && (
              <span className="location-item">
                {firstSegment.pickupAddress} &nbsp;-&gt;&nbsp; {firstSegment.dropoffAddress}
                {isRoundTrip && ' (Round Trip)'}
              </span>
            )}
          </div>

          {firstSegment?.flightName && (
            <p className="location-item" style={{ marginTop: '0.5rem' }}>
              <b>Flight Name: </b>&nbsp;
              {firstSegment.flightName}
            </p>
          )}
        </div>
      </div>

      <div className={`${style.rentalLocation__item} ${style['cost']}`}>
        <p>
          <strong>€{totalCost.amount.toFixed(2)}</strong>
        </p>
      </div>
    </div>
  )
}

export default BookingCard
